/**
 * Client mirror of the backend RBAC resolver (backend/src/lib/access.ts). Drives
 * UX gating only — every gate that matters is enforced on the server. Reads the
 * effective `caps` + `cardDepths` that /auth/me attaches to the user.
 */
import type { Depth, Persona, User } from '../types/api';
import { DEPTHS } from './capabilities';

export interface CanCtx {
  card?: string;
  drafter?: string;
}

export function depthGE(have: Depth | undefined, need: Depth): boolean {
  return DEPTHS.indexOf(have ?? 'none') >= DEPTHS.indexOf(need);
}

export function capMatches(granted: string, cap: string): boolean {
  if (granted === '*') return true;
  if (granted === '*.view') return cap.endsWith('.view');
  if (granted.endsWith('.*')) {
    const prefix = granted.slice(0, -2);
    return cap === prefix || cap.startsWith(prefix + '.');
  }
  return cap === granted;
}

export type CanFn = (cap: string, ctx?: CanCtx) => boolean;

/** Build a `can(cap, ctx)` function for a user (null → deny all). */
export function buildCan(user: User | null): CanFn {
  const caps = user?.caps ?? [];
  const cardDepth = (persona: string): Depth => user?.cardDepths?.[persona as Persona] ?? 'none';
  return (cap: string, ctx: CanCtx = {}): boolean => {
    if (!user) return false;
    if (cap.startsWith('card.')) {
      const parts = cap.split('.');
      return depthGE(cardDepth(parts[1] ?? ''), (parts[2] as Depth) || 'view');
    }
    if (cap === 'integrations.approvals.decide' && ctx.drafter && ctx.drafter === user.id) return false;
    if (ctx.card && cardDepth(ctx.card) === 'none') return false;
    return caps.some((p) => capMatches(p, cap));
  };
}

export type CardState = 'unlocked' | 'viewonly' | 'locked' | 'hidden';

/** Home exec-card state from the user's per-card depth (mirrors demo homeCardState). */
export function cardStateFor(user: User | null, persona: string): CardState {
  const d: Depth = user?.cardDepths?.[persona as Persona] ?? 'none';
  const isGuest = user?.role === 'guest';
  if (isGuest) return d === 'none' ? 'hidden' : d === 'view' ? 'viewonly' : 'unlocked';
  return d === 'none' ? 'locked' : d === 'view' ? 'viewonly' : 'unlocked';
}
